import { dcChargingPortGroups } from "./component-ports";
import { applyStateToAmp, getEffectiveStateOption } from "./simulate/states";
import type { ComponentDefinition, ComponentNode } from "./types";

type ChargeState = { componentStates: Record<string, string>; loadStates?: Record<string, { active: boolean }> };

/** The model supports an entered charging ceiling on a declared charger stage.
 * This does not establish which controls a physical device exposes. */
export function chargeCurrentMaximum(def: ComponentDefinition): number | undefined {
  return def.category === "charger" && typeof def.ampOutput === "number" &&
    Number.isFinite(def.ampOutput) && def.ampOutput > 0 &&
    dcChargingPortGroups(def).some(port => port.some(t => t.polarity === "positive") &&
      port.some(t => t.polarity === "negative" || t.polarity === "ground"))
    ? def.ampOutput : undefined;
}

export function supportsChargeCurrentLimit(def: ComponentDefinition): boolean {
  return chargeCurrentMaximum(def) !== undefined;
}

/** Absent uses the hardware rating. Bound stale values after a rating edit;
 * malformed values never manufacture a new output rating. */
export function configuredChargeCurrent(node: Pick<ComponentNode, "chargeCurrentLimitA">, def: ComponentDefinition): number | undefined {
  const maximum = chargeCurrentMaximum(def);
  if (maximum === undefined) return undefined;
  const limit = node.chargeCurrentLimitA;
  return typeof limit === "number" && Number.isFinite(limit) && limit >= 0 ? Math.min(limit, maximum) : maximum;
}

/** Only live operation uses the instance ceiling. Design callers omit simulate
 * and keep the rated envelope. The optional base retains existing typical-output
 * assumptions; a configured ceiling never increases that estimate. */
export function effectiveChargeCurrent(
  node: Pick<ComponentNode, "id" | "chargeCurrentLimitA">,
  def: ComponentDefinition,
  simulate?: ChargeState,
  baseA: number | undefined = def.ampOutput,
): number | undefined {
  if (!simulate) return baseA;
  const current = applyStateToAmp(baseA, getEffectiveStateOption(def, node.id, simulate), "output");
  const limit = configuredChargeCurrent(node, def);
  return current === undefined || limit === undefined ? current : Math.min(Math.max(0, current), limit);
}
